import React from "react";
import ReactDOM from "react-dom/client";
import { createBrowserRouter, RouterProvider, Navigate } from "react-router-dom";

// Self-hosted fonts (needed so COEP: require-corp doesn't block Google Fonts)
import "@fontsource/space-grotesk/400.css";
import "@fontsource/space-grotesk/500.css";
import "@fontsource/space-grotesk/600.css";
import "@fontsource/space-grotesk/700.css";
import "@fontsource/jetbrains-mono/400.css";

import "./index.css";
import Record from "./pages/Record";
import AdminLogin from "./pages/AdminLogin";
import Admin from "./pages/Admin";
import AdminCrmLayout from "./pages/AdminCrmLayout";
import AdminVideo from "./pages/AdminVideo";
import BestOf from "./pages/BestOf";
import CrmLeadDetail from "./pages/crm/CrmLeadDetail";
import CrmLeadsList from "./pages/crm/CrmLeadsList";
import CrmSectionPlaceholder from "./pages/crm/CrmSectionPlaceholder";
import CrmToday from "./pages/crm/CrmToday";
import CrmTraces from "./pages/crm/CrmTraces";
import Home from "./pages/Home";
import AvatarBuilder from "./pages/AvatarBuilder";
import AvatarSessionShare from "./pages/AvatarSessionShare";
import DevRenderPrompter from "./pages/DevRenderPrompter";
import Mockup from "./pages/Mockup";
import Creations from "./pages/Creations";

// When served from a japan* vanity sub-domain, the site root IS the private
// creations gallery (same prod distribution, hostname-based routing).
// japan1/2/3 are pre-provisioned spares so future galleries need no infra
// redeploy — just route them here (or to other pages) and ship the frontend.
const isJapan =
  typeof window !== "undefined" &&
  ["japan", "japan1", "japan2", "japan3"].includes(
    window.location.hostname.split(".")[0]
  );

const router = createBrowserRouter([
  { path: "/", element: isJapan ? <Creations /> : <Home /> },
  // Private creations gallery — also reachable at /japan on the main domain.
  { path: "/japan", element: <Creations /> },
  { path: "/r/:linkId", element: <Record /> },
  { path: "/best-of", element: <BestOf /> },
  { path: "/builder", element: <AvatarBuilder /> },
  { path: "/avatar/sessions/:sessionId/share", element: <AvatarSessionShare /> },
  { path: "/admin", element: <Admin /> },
  { path: "/admin/login", element: <AdminLogin /> },
  {
    path: "/admin/crm",
    element: <AdminCrmLayout />,
    children: [
      { index: true, element: <CrmToday /> },
      {
        path: "estrellas",
        element: (
          <CrmSectionPlaceholder
            title="Estrellas"
            description="Vue prioritaire des contacts chauds — données Dynamo à modéliser (prochain sprint)."
          />
        ),
      },
      {
        path: "touchpoints",
        element: (
          <CrmSectionPlaceholder
            title="Touchpoints"
            description="Historique des interactions — à brancher sur les entités touchpoint."
          />
        ),
      },
      {
        path: "batches",
        element: (
          <CrmSectionPlaceholder
            title="Batches"
            description="Préparation et envoi de newsletters / lots — à venir."
          />
        ),
      },
      { path: "leads", element: <CrmLeadsList /> },
      { path: "leads/:leadId", element: <CrmLeadDetail /> },
      { path: "traces", element: <CrmTraces /> },
      {
        path: "suggestions",
        element: (
          <CrmSectionPlaceholder
            title="Suggestions"
            description="Enrichissement (ex. Gmail) — hors scope du sprint actuel."
          />
        ),
      },
      {
        path: "exited",
        element: (
          <CrmSectionPlaceholder
            title="Sortis"
            description="Contacts sortis du pipeline — à venir."
          />
        ),
      },
      {
        path: "settings",
        element: (
          <CrmSectionPlaceholder
            title="Réglages"
            description="Préférences CRM — à venir."
          />
        ),
      },
    ],
  },
  // Legacy redirect for old AdminList path
  { path: "/admin/videos", element: <Navigate to="/admin#videos" replace /> },
  { path: "/admin/videos/:videoId", element: <AdminVideo /> },
  // Local POC route — not linked from anywhere, used by Playwright capture.
  { path: "/dev/render-prompter", element: <DevRenderPrompter /> },
  // Internal design gallery — caption mode mockups for the LLYLL Subtitler.
  { path: "/mockup", element: <Mockup /> },
]);

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <RouterProvider router={router} />
  </React.StrictMode>
);
